import React, { useState, useRef, useEffect } from 'react';
import './Dropdown.scss';

export const Dropdown = ({
  trigger,
  children,
  isOpen,
  onToggle,
  className = '',
  menuClassName = '',
  position = 'bottom-left',
  closeOnClickOutside = true,
  closeOnItemClick = true,
  ...props
}) => {
  const dropdownRef = useRef(null);
  const menuRef = useRef(null);
  const [actualPosition, setActualPosition] = useState(position);

  useEffect(() => {
    if (!closeOnClickOutside || !isOpen) return;

    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onToggle(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onToggle, closeOnClickOutside]);

  // Détection de la position optimale pour éviter le débordement
  useEffect(() => {
    if (!isOpen || !dropdownRef.current || !menuRef.current) return;

    const dropdownRect = dropdownRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;

    let newPosition = position;

    // Vérifier si le dropdown dépasse en bas
    const spaceBelow = viewportHeight - dropdownRect.bottom;
    const spaceAbove = dropdownRect.top;
    const menuHeight = menuRect.height;

    // Si pas assez d'espace en bas mais assez en haut, passer en "top"
    if (spaceBelow < menuHeight && spaceAbove > menuHeight) {
      if (position.includes('bottom')) {
        newPosition = position.replace('bottom', 'top');
      }
    }
    // Si pas assez d'espace en haut mais assez en bas, rester en "bottom"
    else if (spaceAbove < menuHeight && spaceBelow > menuHeight) {
      if (position.includes('top')) {
        newPosition = position.replace('top', 'bottom');
      }
    }

    // Vérifier si le dropdown dépasse à droite
    const spaceRight = viewportWidth - dropdownRect.left;
    const spaceLeft = dropdownRect.right;
    const menuWidth = menuRect.width;

    // Si pas assez d'espace à droite mais assez à gauche, passer en "right"
    if (spaceRight < menuWidth && spaceLeft > menuWidth) {
      if (newPosition.includes('left')) {
        newPosition = newPosition.replace('left', 'right');
      }
    }
    // Si pas assez d'espace à gauche mais assez à droite, rester en "left"
    else if (spaceLeft < menuWidth && spaceRight > menuWidth) {
      if (newPosition.includes('right')) {
        newPosition = newPosition.replace('right', 'left');
      }
    }

    setActualPosition(newPosition);
  }, [isOpen, position]);

  const handleItemClick = (callback) => {
    return (event) => {
      if (callback) callback(event);
      if (closeOnItemClick) onToggle(false);
    };
  };

  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && child.props.onClick) {
      return React.cloneElement(child, {
        onClick: handleItemClick(child.props.onClick)
      });
    }
    return child;
  });

  return (
    <div
      ref={dropdownRef}
      className={`dropdown ${className}`}
      {...props}
    >
      <div
        className="dropdown__trigger"
        onClick={() => onToggle(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(!isOpen);
          }
        }}
      >
        {trigger}
      </div>

      {isOpen && (
        <div
          ref={menuRef}
          className={`dropdown__menu dropdown__menu--${actualPosition} ${menuClassName}`}
          role="menu"
        >
          {enhancedChildren}
        </div>
      )}
    </div>
  );
};

export default Dropdown;